import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import PropertyOwnersTabSkeleton from '@/components/v2/molecules/admin/tabs/UserManagementTabs/PropertyOwners/PropertyOwnersTabSkeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useUsers } from '@/hooks/useUsers';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import TabWarningBanner from '../../../../../molecules/admin/shared/TabWarningBanner';
import ManagedUserCard from '../../../../../molecules/admin/tabs/shared/ManagedUserCard';
import CreateUserModal from '../../shared/CreateUserModal';
import AssignPropertiesModal from './AssignPropertiesModal';

const PropertyOwnersTab = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<any>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  const { user, profile } = useAuth();

  const {
    data: owners = [],
    isLoading: isLoadingOwners,
    error: errorOwners,
    refetch: refetchOwners,
  } = useUsers('property_owner');

  const { resendInvitation } = useUserManagement();

  const openCreateModal = () => setShowCreateModal(true);
  const closeCreateModal = () => setShowCreateModal(false);

  const openAssignModal = (owner: any) => {
    setSelectedOwner(owner);
    setShowAssignModal(true);
  };

  const closeAssignModal = () => {
    setSelectedOwner(null);
    setShowAssignModal(false);
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingUserId(userId);
    await resendInvitation(userId);
    setResendingUserId(null);
  };

  const handleCreateUserSuccess = async (createdUserId: string) => {
    closeCreateModal();

    const { data: updatedOwners = [] } = await refetchOwners();

    const newOwner = updatedOwners.find(
      (owner) => owner.user_id === createdUserId,
    );

    if (newOwner) {
      openAssignModal(newOwner);
    }
  };

  const handleAssignSuccess = () => {
    closeAssignModal();
    refetchOwners();
  };

  if (!user || !profile || profile.role !== 'super_admin') {
    return (
      <TabWarningBanner
        title="Acceso denegado"
        message="Solo los super administradores pueden acceder a esta sección."
      />
    );
  }

  if (isLoadingOwners) return <PropertyOwnersTabSkeleton />;

  if (errorOwners) {
    return (
      <TabErrorBanner
        title="Error al cargar propietarios"
        message="No se pudieron cargar los propietarios. Por favor, intenta nuevamente."
        onRetry={() => refetchOwners()}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Dueños de Propiedades
          </h2>
          <p className="text-gray-600">
            Gestiona los propietarios y sus propiedades asignadas
          </p>
        </div>

        <Button
          className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
          onClick={openCreateModal}
        >
          <Plus className="w-4 h-4 mr-2" />
          Crear Propietario
        </Button>
      </div>

      <div className="grid gap-4">
        {!owners || owners.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">No hay propietarios registrados</p>
            </CardContent>
          </Card>
        ) : (
          owners.map((owner) => (
            <ManagedUserCard
              key={owner.id}
              user={owner}
              onAssign={openAssignModal}
              onResendInvitation={handleResendInvitation}
              isLoading={resendingUserId === owner.user_id}
            />
          ))
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
        onSuccess={handleCreateUserSuccess}
        userRole="property_owner"
      />

      {selectedOwner && (
        <AssignPropertiesModal
          isOpen={showAssignModal}
          onClose={closeAssignModal}
          onSuccess={handleAssignSuccess}
          user={selectedOwner}
        />
      )}
    </div>
  );
};

export default PropertyOwnersTab;
